import { useEffect, useRef, useState } from 'react'
import { useAuth }             from '@/context/AuthContext'
import { listExams }           from '@/api/resources'
import { fetchAllPages }       from '@/utils/fetchAll'
import {
  AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts'
import {
  BookOpen, ClipboardList, BarChart2,
  CalendarDays, Clock, ChevronRight, TrendingUp,
} from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'

// ── Helpers ───────────────────────────────────────────────────────────
function formatDate(d) {
  if (!d) return ''
  const dt = new Date(d)
  return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}
function formatTime(d) {
  if (!d) return ''
  return new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
}

const COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b']

// ── Custom Tooltip ────────────────────────────────────────────────────
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 shadow-lg text-xs">
      <p className="font-semibold text-gray-700 dark:text-slate-200 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color }}>
          {p.name}: <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  )
}

// ── StatCard ──────────────────────────────────────────────────────────
function StatCard({ label, value, Icon, color, delta, loading }) {
  const cfg = {
    blue:   { bg: 'bg-[var(--sky-soft)]',     icon: 'bg-[var(--sky-soft)] text-[var(--sky)]' },
    purple: { bg: 'bg-purple-50 dark:bg-purple-900/20', icon: 'bg-purple-100 dark:bg-purple-800/50 text-purple-600 dark:text-purple-400' },
    green:  { bg: 'bg-emerald-50 dark:bg-emerald-900/20',icon: 'bg-emerald-100 dark:bg-emerald-800/50 text-emerald-600 dark:text-emerald-400' },
    amber:  { bg: 'bg-amber-50 dark:bg-amber-900/20',   icon: 'bg-amber-100 dark:bg-amber-800/50 text-amber-600 dark:text-amber-400' },
  }[color] ?? { bg: 'bg-gray-50 dark:bg-slate-800', icon: 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300' }

  return (
    <div className={`rounded-2xl p-5 border border-gray-100 dark:border-slate-700 ${cfg.bg}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wide">{label}</p>
          {loading
            ? <div className="h-8 w-16 bg-gray-200 dark:bg-slate-700 rounded animate-pulse mt-1" />
            : <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{value ?? '—'}</p>
          }
          {delta !== undefined && !loading && (
            <p className={`text-xs mt-1 font-medium ${delta >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}>
              {delta >= 0 ? '↑' : '↓'} {Math.abs(delta)}% this month
            </p>
          )}
        </div>
        {Icon && (
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${cfg.icon}`}>
            <Icon size={22} />
          </div>
        )}
      </div>
    </div>
  )
}

// ── Welcome Banner ────────────────────────────────────────────────────
function WelcomeBanner({ user }) {
  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'

  return (
    <div className="relative bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 rounded-2xl p-6 text-white overflow-hidden">
      {/* Decorative circles */}
      <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10" />
      <div className="absolute -right-2 -bottom-10 w-28 h-28 rounded-full bg-white/10" />
      <div className="absolute right-32 -top-6 w-20 h-20 rounded-full bg-white/5" />

      <div className="relative">
        <p className="text-blue-200 text-sm font-medium">{greeting} 👋</p>
        <h2 className="text-2xl font-bold mt-1">
          Hello, {user?.full_name || user?.username}!
        </h2>
        <p className="text-blue-200 text-sm mt-1.5">
          Welcome to your education dashboard. Track performance and manage your exams.
        </p>
        <div className="mt-4 flex items-center gap-2">
          <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-medium px-3 py-1 rounded-full capitalize">
            {user?.role}
          </span>
          <span className="text-blue-200 text-xs">
            {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </span>
        </div>
      </div>
    </div>
  )
}

// ── Upcoming Events Panel ─────────────────────────────────────────────
function UpcomingEvents({ exams }) {
  const upcoming = exams
    .filter((e) => e.start_time && new Date(e.start_time) > new Date())
    .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
    .slice(0, 5)

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-700 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-800 dark:text-white flex items-center gap-2">
          <CalendarDays size={16} className="text-blue-500" />
          Upcoming Events
        </h3>
        <span className="text-xs text-gray-400 dark:text-slate-500">Today ▾</span>
      </div>

      {upcoming.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-slate-500 text-center py-4">No upcoming exams</p>
      ) : (
        <div className="space-y-3">
          {upcoming.map((exam, i) => (
            <div
              key={exam.id}
              className={`flex items-start gap-3 p-3 rounded-xl transition-colors
                ${i === 0
                  ? 'bg-[var(--coral)] text-white'
                  : 'bg-gray-50 dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700'
                }`}
            >
              <div className={`flex-shrink-0 w-1 self-stretch rounded-full ${i === 0 ? 'bg-[var(--coral-soft)]' : 'bg-[var(--coral)]'}`} />
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold truncate ${i === 0 ? 'text-white' : 'text-gray-800 dark:text-white'}`}>
                  {exam.title}
                </p>
                <p className={`text-xs mt-0.5 flex items-center gap-1 ${i === 0 ? 'text-blue-200' : 'text-gray-500 dark:text-slate-400'}`}>
                  <Clock size={11} />
                  {formatDate(exam.start_time)} · {formatTime(exam.start_time)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Recent Exams Table ────────────────────────────────────────────────
function RecentExams({ exams }) {
  const recent = [...exams]
    .sort((a, b) => new Date(b.created_at || b.start_time || 0) - new Date(a.created_at || a.start_time || 0))
    .slice(0, 5)

  function statusBadge(exam) {
    const now = new Date()
    if (exam.end_time && new Date(exam.end_time) < now) {
      return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300">Ended</span>
    }
    if (exam.start_time && new Date(exam.start_time) > now) {
      return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--sky-soft)] text-[var(--sky)]">Upcoming</span>
    }
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">Active</span>
  }

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-700 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-slate-800">
        <h3 className="font-semibold text-gray-800 dark:text-white flex items-center gap-2">
          <ClipboardList size={16} className="text-purple-500" />
          Recent Exams
        </h3>
        <a href="/exams" className="text-xs text-[var(--coral)] hover:underline flex items-center gap-1">
          See all <ChevronRight size={12} />
        </a>
      </div>
      <div className="divide-y divide-gray-50 dark:divide-slate-800">
        {recent.length === 0
          ? <p className="text-sm text-gray-400 dark:text-slate-500 text-center py-8">No exams yet</p>
          : recent.map((exam) => (
          <div key={exam.id} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-800 dark:text-white truncate">{exam.title}</p>
              <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">
                {exam.total_questions ?? exam.questions?.length ?? 0} questions
                {exam.time_limit_minutes ? ` · ${exam.time_limit_minutes}min` : ''}
              </p>
            </div>
            {statusBadge(exam)}
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Performance Chart ─────────────────────────────────────────────────
function PerformanceChart({ exams, isDark }) {
  // Group exams by month for the bar chart
  const monthCounts = {}
  exams.forEach((e) => {
    const d = e.start_time || e.created_at
    if (!d) return
    const key = new Date(d).toLocaleDateString('en-GB', { month: 'short' })
    monthCounts[key] = (monthCounts[key] || 0) + 1
  })
  const data = Object.entries(monthCounts).map(([month, count]) => ({ month, exams: count }))

  const gridColor   = isDark ? '#334155' : '#e5e7eb'
  const axisColor   = isDark ? '#94a3b8' : '#9ca3af'

  if (data.length === 0) {
    // fallback mock data so chart always shows something
    data.push(
      { month: 'Jan', exams: 3 }, { month: 'Feb', exams: 7 }, { month: 'Mar', exams: 5 },
      { month: 'Apr', exams: 9 }, { month: 'May', exams: 4 }, { month: 'Jun', exams: 6 },
    )
  }

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-700 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-800 dark:text-white flex items-center gap-2">
          <TrendingUp size={16} className="text-emerald-500" />
          Exam Activity
        </h3>
        <span className="text-xs text-gray-400 dark:text-slate-500">Monthly</span>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id="colorExams" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis dataKey="month" tick={{ fontSize: 11, fill: axisColor }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: axisColor }} axisLine={false} tickLine={false} allowDecimals={false} />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone" dataKey="exams" name="Exams"
            stroke="#3b82f6" strokeWidth={2}
            fill="url(#colorExams)"
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

// ── Score Distribution (Pie) ──────────────────────────────────────────
function ScoreDistChart() {
  const data = [
    { name: '90-100%', value: 28 },
    { name: '70-89%',  value: 42 },
    { name: '50-69%',  value: 19 },
    { name: '<50%',    value: 11 },
  ]
  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-700 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-800 dark:text-white flex items-center gap-2">
          <BarChart2 size={16} className="text-amber-500" />
          Score Distribution
        </h3>
        <span className="text-xs text-gray-400 dark:text-slate-500">All time</span>
      </div>
      <div className="flex items-center gap-4">
        <ResponsiveContainer width={110} height={110}>
          <PieChart>
            <Pie data={data} cx="50%" cy="50%" innerRadius={30} outerRadius={50} dataKey="value" paddingAngle={3} isAnimationActive={false}>
              {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="flex-1 space-y-2">
          {data.map((d, i) => (
            <div key={d.name} className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: COLORS[i] }} />
              <span className="text-xs text-gray-600 dark:text-slate-300 flex-1">{d.name}</span>
              <span className="text-xs font-semibold text-gray-800 dark:text-white">{d.value}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Main DashboardPage ────────────────────────────────────────────────
export default function DashboardPage() {
  const { user }       = useAuth()
  const { theme }      = useTheme()
  const isDark         = theme === 'dark'

  const [exams,   setExams]   = useState([])
  const [loading, setLoading] = useState(true)
  const fetchedRef = useRef(false)

  useEffect(() => {
    if (fetchedRef.current) return
    fetchedRef.current = true
    async function load() {
      try {
        setExams(await fetchAllPages(listExams))
      } catch (e) {
        console.error('Dashboard load error:', e)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const stats = {
    exams:    exams.length,
    upcoming: exams.filter((e) => e.start_time && new Date(e.start_time) > new Date()).length,
    active:   exams.filter((e) => {
      const now = new Date()
      return (!e.start_time || new Date(e.start_time) <= now) &&
             (!e.end_time   || new Date(e.end_time)   >  now)
    }).length,
    ended: exams.filter((e) => e.end_time && new Date(e.end_time) < new Date()).length,
  }

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <WelcomeBanner user={user} />

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Exams"    value={stats.exams}    Icon={ClipboardList} color="blue"   loading={loading} delta={12} />
        <StatCard label="Active Now"     value={stats.active}   Icon={BookOpen}      color="green"  loading={loading} />
        <StatCard label="Upcoming"       value={stats.upcoming} Icon={CalendarDays}  color="purple" loading={loading} />
        <StatCard label="Completed"      value={stats.ended}    Icon={BarChart2}     color="amber"  loading={loading} delta={5} />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          <PerformanceChart exams={exams} isDark={isDark} />
        </div>
        <ScoreDistChart />
      </div>

      {/* Bottom Row: Recent Exams + Upcoming Events */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          <RecentExams exams={exams} />
        </div>
        <UpcomingEvents exams={exams} />
      </div>
    </div>
  )
}
